import { CSSProperties, ComponentProps, FC, PropsWithChildren } from "react";

import { Button, MenuButton } from "capitalrx-components-2.0/lib/general";
import { SubmitButton, SubmitButtonProps } from "capitalrx-components-2.0/lib/forms";
import { useEnrollmentContext } from "@/state";

import "./Button.scss";

const getThemeButtonColor = (primaryButtonColor?: string, primaryColor?: string) => primaryButtonColor || primaryColor || "#00a4e4";

const getButtonHoverBackground = (buttonColor: string) => {
  if (!buttonColor.startsWith("#")) {
    return "rgba(0, 164, 228, 0.1)";
  }

  if (buttonColor.length === 7) {
    return `${buttonColor}1A`;
  }

  if (buttonColor.length === 4) {
    const r = buttonColor[1];
    const g = buttonColor[2];
    const b = buttonColor[3];
    return `#${r}${r}${g}${g}${b}${b}1A`;
  }

  return "rgba(0, 164, 228, 0.1)";
};

const getInlineVariableStyle = (buttonColor: string, style?: CSSProperties): CSSProperties => ({
  ...(style || {}),
  ["--themed-button-color" as string]: buttonColor,
  ["--themed-button-color-hover-bg" as string]: getButtonHoverBackground(buttonColor),
});

type BaseButtonProps = ComponentProps<typeof Button>;
type BaseMenuButtonProps = ComponentProps<typeof MenuButton>;

export const ThemedButton: FC<PropsWithChildren<BaseButtonProps>> = (props) => {
  const experienceData = useEnrollmentContext();
  const theme = experienceData?.theme;
  const buttonColor = getThemeButtonColor((theme as { primary_button_color?: string } | undefined)?.primary_button_color, theme?.primary_color);

  const className = ["themed-button", props.className].filter(Boolean).join(" ");

  return <Button {...props} className={className} style={getInlineVariableStyle(buttonColor, props.style)} />;
};

const _ThemedSubmitButton: FC<PropsWithChildren & SubmitButtonProps> = (props) => {
  const experienceData = useEnrollmentContext();
  const theme = experienceData?.theme;
  const buttonColor = getThemeButtonColor((theme as { primary_button_color?: string } | undefined)?.primary_button_color, theme?.primary_color);

  const wrapperClassName = ["themed-submit-button-wrapper", props.className].filter(Boolean).join(" ");

  return (
    <div className={wrapperClassName} style={getInlineVariableStyle(buttonColor, props.style)}>
      <SubmitButton {...props}>{props.children}</SubmitButton>
    </div>
  );
};

export const ThemedSubmitButton = _ThemedSubmitButton;

export const ThemedMenuButton: FC<BaseMenuButtonProps> = (props) => {
  const experienceData = useEnrollmentContext();
  const theme = experienceData?.theme;
  const buttonColor = getThemeButtonColor((theme as { primary_button_color?: string } | undefined)?.primary_button_color, theme?.primary_color);

  const className = ["themed-menu-button", props.className].filter(Boolean).join(" ");

  return <MenuButton {...props} className={className} style={getInlineVariableStyle(buttonColor, props.style)} />;
};
